import { forwardRef, useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import {
  Dialog,
  Disclose,
  DrawerHead,
  Headline,
  Loading,
  Refusal,
  Sparkline,
  Stat,
  Stats,
  Tooltip,
} from "@lopie/kernel-ui";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";

import Schedule from "./Schedule";
import { useDeployments } from "./deploy.jsx";
import {
  OverrideRulesTable,
  ProposeOverrideDialog,
} from "./mappingOverrides.jsx";

/**
 * Which registry table a source is mapped by — the connector, never a
 * specific instance (LO-126, sync-map governance).
 *
 * A copy of `mapping.registry_key`'s one-line rule, needed here because a
 * writer's `source` on this screen can be instance-scoped
 * (`case-management:acme-legal-group`) while a sync-map request's `source`
 * field is always the bare registry key — the kernel refuses anything else.
 * Kept to the same rule rather than reinvented: split on the first colon.
 */
function registryKeyOf(source) {
  return String(source || "").split(":")[0];
}

/**
 * Is data flowing, and can I plug in more (LO-67).
 *
 * This said "not built yet, waiting on LO-48" and that was too pessimistic.
 * Last *run* needs LO-48, but counts by source, the most recent write and the
 * concept registry are already in the table, and between them that is most of
 * a health screen.
 *
 * The discipline the screen has to keep: **a field nothing records shows a
 * reason, never a zero.** A connector that has never run and one that ran and
 * found nothing are different facts, and a dash somebody can hover for "this
 * is not recorded yet" is worth more than a plausible number that is quietly
 * measuring something else.
 */

/**
 * What kind of thing put records here, said for somebody who does not already
 * know the difference.
 *
 * The blurbs were written for the people who built this — "declared in the
 * mapping registry", "wrote through /actions", a parenthetical about an upsert
 * key. Each is accurate and none of them answer the question a reader of this
 * screen is actually asking, which is *where did this data come from and
 * should I expect more of it*.
 */
const KIND = {
  connector: {
    label: "Connector",
    colour: "kernel.permitted",
    blurb:
      "It pulls records in from an outside system on a schedule, and every " +
      "kind of thing it may bring across is agreed in advance.",
  },
  app: {
    label: "App",
    colour: "primary.main",
    blurb:
      "Not a connector — an app running here that wrote records itself, as " +
      "part of doing its job.",
  },
  unknown: {
    label: "Unrecognised",
    colour: "kernel.attention",
    blurb:
      "Records here name a source that is neither a registered connector nor " +
      "a known app. Not necessarily wrong, and worth knowing about.",
  },
};

function Unknown({ reason }) {
  return (
    <Tooltip title={reason}>
      <Stack direction="row" spacing={0.5} component="span" sx={{ alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" component="span">
          not recorded
        </Typography>
        <HelpIcon sx={{ fontSize: 14, color: "text.secondary" }} />
      </Stack>
    </Tooltip>
  );
}

function Fact({ label, children }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function relative(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * When the counts on this screen were taken (LO-231).
 *
 * The kernel used to count every writer's records by reading the whole table
 * for each request, and at about 100,000 records that stopped fitting the API's
 * 30 seconds. A scheduled job counts now and the screen reads what it stored,
 * so a figure here is as old as the job's last run. That is said on the figure
 * and on every card, because a count a few hours old that does not say so is
 * believed to be now. `state` is the kernel's own word for it: `live` (counted
 * for this request), `stored` (a job's sum, with a time) or `not_run` (nobody
 * has counted, which is not the same as nothing having been written).
 */
function tallyEyebrow(tally) {
  if (!tally || tally.state === "live") return "In the graph right now";
  if (tally.state === "not_run") return "Not counted yet";
  return `Counted ${relative(tally.as_of)}`;
}

/** What the last run did, and why the numbers differ. */
// Lined up and grouped. These sit side by side and get compared to each
// other — 2,503 against 2,308 is a glance, 2503 against 2308 is a squint.
const Count = forwardRef(function Count({ value, fallback = 0, ...rest }, ref) {
  return (
    <Typography
      ref={ref}
      variant="body2"
      sx={{ fontVariantNumeric: "tabular-nums" }}
      {...rest}
    >
      {value == null ? fallback : Number(value).toLocaleString()}
    </Typography>
  );
});

/** A duration a person can hold in their head.
 *
 * The full Xero reconcile takes 592 seconds. "593s" is technically the number
 * and nobody reads it as "ten minutes", which is the fact that matters —
 * Lambda stops at 900. */
function took(seconds) {
  if (seconds == null) return "—";
  const n = Number(seconds);
  if (n < 90) return `${Math.round(n)}s`;
  const minutes = Math.floor(n / 60);
  return `${minutes}m ${String(Math.round(n % 60)).padStart(2, "0")}s`;
}

/**
 * Undo this run (LO-153): restore what it updated, tombstone what it
 * created. A real write against real records, so it is confirmed like one
 * rather than a one-click toggle — same reasoning `RequestCard`'s approve
 * and reject dialogs use, one domain over.
 *
 * `mayGovern` is `undefined` while the connectors response is still loading
 * and `false` once it has answered and the caller is not on the governor
 * list — the button is disabled either way, with a tooltip that says which,
 * because a disabled control with no reason is worse than the honest "you
 * may not" it replaces.
 */
function RollbackControl({ source, runId, mayGovern, onRollback }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null); // { ok, detail }

  async function confirm() {
    setBusy(true);
    try {
      const result = await onRollback(source, runId);
      const restored = result?.restored?.length || 0;
      const tombstoned = result?.tombstoned?.length || 0;
      const edges = result?.edges_found?.length || 0;
      setOutcome({
        ok: true,
        detail: `Restored ${restored}, tombstoned ${tombstoned}`
          + (edges ? `, left ${edges} edge(s) untouched — see below` : "."),
      });
    } catch (e) {
      setOutcome({ ok: false, detail: e?.body?.detail || e?.message || String(e) });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <>
      <Tooltip
        title={
          mayGovern === false
            ? "You are not on this kernel's governor list — a decision this " +
              "consequential needs a second person on top of the app's own " +
              "permission, same as approving a manifest request."
            : "Restores every record this run updated to what it replaced, " +
              "and tombstones every record it created. An edge this run " +
              "wrote is left in place — see the Data quality screen for why."
        }
      >
        <span>
          <Button
            size="small"
            color="warning"
            disabled={mayGovern !== true || busy}
            onClick={() => setOpen(true)}
          >
            Roll back
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)}
        maxWidth="sm" fullWidth aria-labelledby="rollback-title">
        <DialogTitle id="rollback-title">Roll back this run</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="warning" variant="outlined" icon={false}>
              <Typography variant="body2">
                Restores every record this run updated to the state it
                replaced, and tombstones every record it created from
                nothing. A record a later run has since touched is left
                alone — this can only undo what this run's effect still is.
              </Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Run <code>{runId}</code> on <code>{source}</code>.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={busy}
            onClick={confirm}>
            {busy ? "Rolling back…" : "Roll back"}
          </Button>
        </DialogActions>
      </Dialog>

      {outcome ? (
        <Alert
          severity={outcome.ok ? "success" : "error"}
          variant="outlined"
          onClose={() => setOutcome(null)}
          sx={{ mt: 1 }}
        >
          {outcome.detail}
        </Alert>
      ) : null}
    </>
  );
}

function LastRun({ state, runs, source, mayGovern, onRollback }) {
  const last = runs[0];
  if (!last) return null;

  const rules = Object.entries(last.rejects_by_rule || {});

  return (
    <>
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          Last run
        </Typography>
        {last.run_id ? (
          <RollbackControl
            source={source}
            runId={last.run_id}
            mayGovern={mayGovern}
            onRollback={onRollback}
          />
        ) : null}
      </Stack>

      <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", mb: 1.5 }}>
        {/* Read and written are different numbers and the gap is the point:
            a delta sync that pulls a thousand and changes two is healthy, and
            "written: 2" alone reads like almost nothing happened. */}
        <Fact label="Read from source">
          <Count value={last.read} fallback="—" />
        </Fact>
        <Fact label="Created">
          <Count value={last.created} />
        </Fact>
        <Fact label="Updated">
          <Count value={last.updated} />
        </Fact>
        <Fact label="Unchanged">
          <Tooltip title="Read but not written — normal for a delta sync, and the reason a low write count is not a problem on its own.">
            <Count value={last.unchanged} />
          </Tooltip>
        </Fact>
        <Fact label="Rejected">
          <Count value={last.rejected} />
        </Fact>
        <Fact label="Took">
          <Typography variant="body2">
            {took(last.duration_seconds)}
          </Typography>
        </Fact>
      </Stack>

      {last.error ? (
        <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }}>
          {last.error}
        </Alert>
      ) : null}

      {rules.length ? (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
            Why records were rejected
          </Typography>
          {/* Its own scroll container. A rule name is as long as it is and
              the page body never scrolls sideways. */}
          <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableBody>
              {rules
                .sort((a, b) => b[1] - a[1])
                .map(([rule, count]) => (
                  <TableRow key={rule}>
                    <TableCell sx={{ border: 0, width: 80 }}>
                      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {count}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ border: 0, fontFamily: "monospace" }}>
                      {rule}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          </Box>
          {/* The reason `reason` names a rule rather than a symptom (LO-11).
              "194 bank transfers that are not payments" is a shrug; "195
              records failed" is an afternoon. */}
          <Typography variant="caption" color="text.secondary">
            Each names the rule that stopped the record, not a symptom — so a
            count here is something you can decide about rather than
            investigate.
          </Typography>
        </>
      ) : null}
    </>
  );
}

/** `2,308`, not `2308`. Counted things want commas. */
const commas = (n) => Number(n || 0).toLocaleString("en-US");

/** The cadence in one phrase, for the front of the card. */
function cadenceOf(schedule) {
  if (!schedule) return "no schedule";
  if (schedule.enabled === false) return "off — runs when somebody runs it";
  const parts = [
    schedule.delta_minutes ? `delta every ${schedule.delta_minutes}m` : null,
    schedule.full_hours ? `full every ${schedule.full_hours}h` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "on, no cadence set";
}

/**
 * One writer, at a glance (LO-111).
 *
 * ## What moved, and why the machinery went first
 *
 * This card carried six labelled facts, a six-column run breakdown, a table
 * of rejection rules, a schedule editor with four form controls, and a
 * four-column mapping table — per writer, six writers, two thousand pixels.
 * The question the screen is named after is *is data flowing*, and answering
 * it meant scrolling past two forms to compare the third connector with the
 * first.
 *
 * So the card answers that question — how much came in, how much was
 * refused, when it last wrote, how often it runs — and the drawer holds every
 * control and every table. The editor in particular is better there: a form
 * inside a card inside a list is a form you can submit while looking at
 * another connector's numbers.
 *
 * **`never written` stays on the card.** Configured and silent is the case
 * somebody needs to see, and it is exactly the one a list of "sources with
 * rows" would hide.
 */
function WriterCard({ writer, self, onOpen, tally }) {
  const state = writer.state;
  // Records and last write are the tally's; run state, schedule and runs are read now.
  const counted = tally?.state !== "not_run";
  const asOf = tally?.state === "stored" ? relative(tally.as_of) : null;
  const kind = KIND[writer.kind] || KIND.unknown;
  const last = (writer.runs || [])[0];
  // An app id is opaque, and the one id this page can resolve without a
  // lookup is its own. Manifests carry no display name — worth fixing when
  // LO-66 builds /apps, and worth not pretending to solve here.
  const isSelf = self && writer.source === self;

  return (
    <Paper
      component="button"
      type="button"
      onClick={() => onOpen(writer)}
      aria-label={`Open ${writer.source}`}
      variant="outlined"
      sx={{
        p: 2,
        minWidth: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        cursor: "pointer",
        backgroundColor: "background.paper",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", minWidth: 0 }}
      >
        {/* The name first, the id second, and the id only when they differ.
            `source_system` for anything written through /actions is the
            Cognito client id, so this card used to read
            `36klgglfh5kclt681c288ag84n` and leave a reader to work out that
            it is Ledger Review. The kernel resolves the manifest to decide
            the row is an app at all, so the name was always available and
            simply was not asked for. The id stays, smaller, because it is
            what appears in an audit row. */}
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="h2"
                      sx={{ fontFamily: "monospace", fontSize: "1rem" }}>
            {writer.label || writer.app_type || writer.source}
          </Typography>
          {(writer.label || writer.app_type) && writer.source !== writer.app_type ? (
            <Tooltip title="The Cognito client id this app writes as. It is what `source_system` carries on its rows and what an audit entry names.">
              <Typography variant="caption" color="text.secondary"
                          sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                {writer.source}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
        {isSelf ? (
          <Tooltip title="This console, writing through /actions like any other app.">
            <Chip size="small" label="this console" color="primary" variant="outlined" />
          </Tooltip>
        ) : null}
        <Tooltip title={kind.blurb}>
          <Chip
            size="small"
            label={kind.label}
            variant="outlined"
            sx={{ color: kind.colour, borderColor: kind.colour }}
          />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {counted && !writer.has_written ? (
          // Configured and silent is the case somebody needs to see, and it
          // is the one a list of "sources with rows" would hide. Not said when
          // nothing has been counted: "never written" would then be a claim
          // about a table nobody has read.
          <Chip
            size="small"
            label="never written"
            sx={{ color: "kernel.attention", borderColor: "kernel.attention" }}
            variant="outlined"
          />
        ) : null}
      </Stack>

      <Stats sx={{ columnGap: 2 }}>
        <Stat
          value={counted && writer.has_written ? commas(writer.total) : "—"}
          label={asOf ? `records · ${asOf}` : counted ? "records · now" : "records · not counted"}
          title={
            asOf
              ? `Records of this writer's making in the graph when the kernel last counted, ${asOf} — the kernel reading its own graph, not the writer reporting what it did. A record written since is not in this figure.`
              : counted
                ? "Records of this writer's making that exist in the graph right now — the kernel reading its own graph, not the writer reporting what it did."
                : "The kernel has not counted what each writer has put in the graph yet, so this is not zero, it is unknown."
          }
        />
        {writer.kind === "connector" ? (
          <Stat
            value={last ? commas(last.rejected) : "—"}
            label="rejected · last run"
            tone={last?.rejected ? "attention" : undefined}
            title={
              last
                ? "Records the mapping rules stopped. Not a silent loss — each one names the rule that stopped it, in the drawer."
                : "No run has been recorded, so there is nothing to have rejected. That is different from a run that rejected none."
            }
          />
        ) : null}
        <Stat
          value={!counted ? "—" : writer.last_write ? relative(writer.last_write) : "never"}
          label={asOf ? "last write · at count" : "last write"}
          title={
            !counted
              ? "Not counted yet."
              : writer.last_write
                ? `${writer.last_write}${asOf ? ` — as of the count, ${asOf}` : ""}`
                : "This writer has never written a record."
          }
        />
      </Stats>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: "auto",
          pt: 1.25,
          borderTop: 1,
          borderColor: "divider",
          flexWrap: "wrap",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {writer.kind === "connector"
            ? cadenceOf(writer.schedule)
            : "writes through /actions"}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* The last few runs, written against read. Genuinely a sequence —
            `runs` is a list of recorded runs in order — and the shape says
            something a number cannot: a connector that wrote two thousand
            once and nothing since looks nothing like one that writes twenty
            every hour, and both report the same total. */}
        {(writer.runs || []).length > 1 ? (
          <Box sx={{ width: 84 }}>
            <Sparkline
              values={[...writer.runs].reverse().map((r) => r.written || 0)}
              labels={[...writer.runs]
                .reverse()
                .map((r) => `${String(r.at || r.started_at || "").slice(0, 10)} — written`)}
              height={18}
              label={`Records written on each of the last ${writer.runs.length} recorded runs, oldest first.`}
            />
          </Box>
        ) : null}
        {writer.kind === "connector" && state ? (
          <Chip
            size="small"
            variant="outlined"
            label={state.last_outcome}
            sx={{
              color:
                state.last_outcome === "ok"
                  ? "kernel.permitted"
                  : "kernel.refused",
              borderColor:
                state.last_outcome === "ok"
                  ? "kernel.permitted"
                  : "kernel.refused",
            }}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * One approved sync mapping, and the switch that flips its own `enabled`
 * flag (LO-126 step 3).
 *
 * The interaction shape is `Schedule`'s: a local value seeded from the prop,
 * a `busy` flag that disables the control while the write is in flight, and
 * a `failure` rendered inline rather than thrown. It differs from `Schedule`
 * in one way only — there is a single field and nothing to batch, so the
 * switch writes on `onChange` rather than waiting for a Save button, which
 * is the same reason the schedule's own "delta every" field does not have
 * one either when nothing else on the form changed.
 *
 * There is no live read to reconcile against afterwards. Unlike the cadence
 * switch — which reloads `GET /connectors` and shows what the kernel now
 * holds rather than what was asked for — nothing in this API surface lists
 * a source's *current* live mappings; what this screen knows about approved
 * mappings comes from `GET /connector-map-requests`, filtered to `approved`,
 * and that request's own `declaration.enabled` is what was true at approval
 * time, not now. So a successful toggle updates local state directly from
 * the toggle response, which is honest about what it is: a write with no
 * corresponding read to confirm it against, the same asymmetry
 * `store.get_syncmap` vs. the request queue already has server-side.
 */
function SyncMapRow({ source, mapping, post, onToggled }) {
  const [enabled, setEnabled] = useState(Boolean(mapping.enabled));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    setEnabled(Boolean(mapping.enabled));
  }, [mapping.enabled]);

  async function toggle(next) {
    setBusy(true);
    setFailure(null);
    try {
      const result = await post(
        `/connectors/${encodeURIComponent(source)}/syncmaps/${encodeURIComponent(mapping.table)}/toggle`,
        { enabled: next },
      );
      setEnabled(Boolean(result.enabled));
      onToggled(mapping.table, Boolean(result.enabled));
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Switch
          size="small"
          checked={enabled}
          disabled={busy}
          onChange={(e) => toggle(e.target.checked)}
          slotProps={{ input: { "aria-label": `Sync ${mapping.table}` } }}
        />
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
          {mapping.table}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          &rarr; {mapping.target_type}
        </Typography>
        {mapping.tag ? (
          <Chip size="small" variant="outlined" label={mapping.tag} />
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {enabled ? "On" : "Off — approved, not syncing"}
        </Typography>
      </Stack>
      {failure ? (
        <Typography
          variant="caption"
          sx={{ color: "kernel.refused", display: "block", mt: 0.5 }}
        >
          {failure.message}
        </Typography>
      ) : null}
    </Paper>
  );
}

/**
 * Propose a new table-to-type sync mapping (LO-126 step 3).
 *
 * A `Dialog` with a form, on purpose and no fancier: the mapping itself is
 * data (`source column -> kernel field`, one for one — see `sync_maps.py`'s
 * module docstring for why nothing cleverer is expressible here), so the
 * builder is exactly that data typed in. Submitting proposes; it grants
 * nothing and a governor still has to approve it on Pending apps before any
 * run reads the table differently.
 *
 * The target type is fetched from `GET /schema`, never hardcoded — a type
 * approved five minutes ago through the object-type queue appears in this
 * list with no change to this file, which is the same test the rest of this
 * console holds every screen to.
 *
 * **Source column used to be a blind text box.** Once a table is chosen,
 * `GET /connectors/{source}/tables/{table}/columns` samples up to 50 live
 * records and offers their real attribute names as suggestions — but the
 * field stays free text (`Autocomplete freeSolo`, never a closed `select`),
 * because the sample is known-incomplete: it is 50 records out of however
 * many the table holds, not a schema, and a column absent from it may still
 * be real. Typing one the sample never saw has to keep working. The caption
 * above the field says so in words rather than leaving that caveat in this
 * comment where nobody proposing a mapping will ever read it.
 *
 * **Table, target type and kernel field are `Autocomplete` too, but closed —
 * no `freeSolo`.** Unlike a source column, these three are always a real,
 * enumerable list (this app's unmapped tables, `GET /schema`'s types, the
 * chosen type's own declared fields) with no equivalent of "the sample missed
 * it": a value not in the list is not a real option, so typing one is search,
 * not an escape hatch. A closed `Autocomplete` gives that search for free —
 * type-to-filter over what used to be a scroll through a plain `select`,
 * which was the whole complaint once `Table` alone had 44 entries.
 */
function MappingBuilder({ open, onClose, get, post, source, tables, onProposed }) {
  const [schema, setSchema] = useState(null);
  const [schemaFailure, setSchemaFailure] = useState(null);
  const [table, setTable] = useState("");
  const [targetType, setTargetType] = useState("");
  const [pairs, setPairs] = useState([{ column: "", field: "" }]);
  const [tag, setTag] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  // Real column names for `table`, from a bounded live sample — `null`
  // before a table is chosen or while the read is in flight, distinct from
  // `discoveredFailure` (the read itself failed) and from a successful read
  // that simply found nothing to offer.
  const [discovered, setDiscovered] = useState(null);
  const [discoveredFailure, setDiscoveredFailure] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTable("");
    setTargetType("");
    setPairs([{ column: "", field: "" }]);
    setTag("");
    setFailure(null);
    setDiscovered(null);
    setDiscoveredFailure(null);
    if (!schema && !schemaFailure) {
      get("/schema").then(setSchema).catch(setSchemaFailure);
    }
    // schema/schemaFailure deliberately excluded: this fetches once per
    // mount and caches for the session, not once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, get]);

  // Sampled once per table chosen, not once per keystroke in the field it
  // feeds — the same "fetch what this view needs, when it needs it" shape
  // `TablesToMap` already uses for `GET /connectors/{source}/tables`.
  useEffect(() => {
    if (!open || !table) {
      setDiscovered(null);
      setDiscoveredFailure(null);
      return;
    }
    let cancelled = false;
    setDiscovered(null);
    setDiscoveredFailure(null);
    get(`/connectors/${encodeURIComponent(source)}/tables/${encodeURIComponent(table)}/columns`)
      .then((body) => {
        if (!cancelled) setDiscovered(body);
      })
      .catch((e) => {
        if (!cancelled) setDiscoveredFailure(e);
      });
    return () => {
      cancelled = true;
    };
  }, [open, table, source, get]);

  const types = schema?.types || [];
  const selectedType = types.find((t) => t.type === targetType);
  const fieldNames = (selectedType?.fields || []).map((f) => f.name);
  const discoveredColumns = discovered?.columns || [];

  function updatePair(i, key, value) {
    setPairs((prev) => prev.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  }
  function addPair() {
    setPairs((prev) => [...prev, { column: "", field: "" }]);
  }
  function removePair(i) {
    setPairs((prev) => prev.filter((_, idx) => idx !== i));
  }

  const fieldMap = Object.fromEntries(
    pairs
      .filter((p) => p.column.trim() && p.field.trim())
      .map((p) => [p.column.trim(), p.field.trim()]),
  );
  const canSubmit = Boolean(table && targetType && Object.keys(fieldMap).length);

  async function submit() {
    setBusy(true);
    setFailure(null);
    try {
      // `source` is instance-qualified for the two reads above — sampling a
      // live table has to know whose account to reach. A sync mapping is
      // per app type, not per instance (the same as `mapping.REGISTRY` is
      // for Xero), so the proposal itself carries the bare key rather than
      // the instance-qualified string this dialog was opened with.
      const body = {
        source: registryKeyOf(source), table, target_type: targetType,
        field_map: fieldMap,
      };
      if (tag.trim()) body.tag = tag.trim();
      const result = await post("/connector-map-requests", body);
      onProposed(result);
      onClose();
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="mapping-title"
    >
      <DialogTitle id="mapping-title">Propose a sync mapping</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined" icon={false}>
            <Typography variant="body2">
              This proposes a mapping; it grants nothing by itself. A governor
              still has to approve it on Pending apps before{" "}
              <code>{registryKeyOf(source)}</code>{" "}
              reads this table any differently than it does today.
            </Typography>
          </Alert>

          <Autocomplete
            size="small"
            options={tables}
            value={table || null}
            onChange={(e, newValue) => setTable(newValue || "")}
            disabled={busy}
            disableClearable={Boolean(table)}
            noOptionsText="No unmapped tables offered"
            renderInput={(params) => <TextField {...params} label="Table" />}
          />

          <Autocomplete
            size="small"
            options={types.map((t) => t.type)}
            value={targetType || null}
            onChange={(e, newValue) => setTargetType(newValue || "")}
            disabled={busy || !schema}
            disableClearable={Boolean(targetType)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target type"
                helperText={schemaFailure ? schemaFailure.message : undefined}
                error={Boolean(schemaFailure)}
              />
            )}
          />

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Source column &rarr; kernel field
            </Typography>
            {/* The sample is known-incomplete, said in words next to the
                field it qualifies rather than left in a doc comment — the
                same "attach it to the thing" rule this console's own
                conventions ask for elsewhere. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              {!table
                ? "Choose a table above to see its real column names."
                : discoveredFailure
                ? `Real column names could not be sampled (${discoveredFailure.message}). Type a source column name directly.`
                : !discovered
                ? "Reading real column names from a live sample…"
                : discoveredColumns.length
                ? `From ${discovered.sampled} sampled record${
                    discovered.sampled === 1 ? "" : "s"
                  } — a column absent from this list may still exist (this is a sample, not a schema). Type one directly if you know it.`
                : `Sampled ${discovered.sampled} record${
                    discovered.sampled === 1 ? "" : "s"
                  } and found no columns to suggest — type a source column name directly.`}
            </Typography>
            <Stack spacing={1}>
              {pairs.map((p, i) => (
                <Stack
                  key={i}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", flexWrap: "wrap" }}
                >
                  <Autocomplete
                    freeSolo
                    size="small"
                    options={discoveredColumns}
                    inputValue={p.column}
                    onInputChange={(e, value) => updatePair(i, "column", value)}
                    disabled={busy}
                    sx={{ minWidth: 160 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Source column" />
                    )}
                  />
                  <Autocomplete
                    size="small"
                    options={fieldNames}
                    value={p.field || null}
                    onChange={(e, newValue) => updatePair(i, "field", newValue || "")}
                    disabled={busy || !targetType}
                    sx={{ minWidth: 160 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Kernel field" />
                    )}
                  />
                  <Button
                    size="small"
                    color="inherit"
                    disabled={busy || pairs.length === 1}
                    onClick={() => removePair(i)}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
              <Button size="small" variant="outlined" onClick={addPair} disabled={busy}>
                Add column
              </Button>
            </Stack>
          </Box>

          <TextField
            size="small"
            label="Tag (optional)"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            disabled={busy}
            sx={{ maxWidth: "20rem" }}
          />

          {failure ? (
            <Alert severity="error" variant="outlined">
              {failure.body?.detail || failure.message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={busy || !canSubmit} onClick={submit}>
          {busy ? "Proposing…" : "Propose mapping"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Every table this connector's reader role can reach, and which of them
 * already has an approved mapping (LO-126 step 3).
 *
 * `GET /connectors/{source}/tables` is fetched per drawer-open rather than
 * once for the whole screen, because it is per-connector and most sessions
 * open at most a couple of drawers — the same "fetch what this view needs,
 * when it needs it" shape `WriterDrawer` already follows for everything
 * else on it.
 */
function TablesToMap({
  get, post, source, mapped, mappedFailure, onToggled, onProposed,
}) {
  const [tables, setTables] = useState(null);
  const [failure, setFailure] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTables(null);
    setFailure(null);
    get(`/connectors/${encodeURIComponent(source)}/tables`)
      .then((body) => {
        if (!cancelled) setTables(body);
      })
      .catch((e) => {
        if (!cancelled) setFailure(e);
      });
    return () => {
      cancelled = true;
    };
  }, [get, source]);

  const mappedByTable = Object.fromEntries(mapped.map((m) => [m.table, m]));
  const unmapped = (tables?.tables || []).filter((t) => !mappedByTable[t]);

  return (
    <>
      <Divider sx={{ mb: 1 }} />
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}
      >
        <Typography variant="overline" color="text.secondary">
          Tables available to map
        </Typography>
        <Button size="small" variant="outlined" onClick={() => setBuilderOpen(true)}>
          Add mapping…
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Every table this connector&apos;s reader role can reach, from{" "}
        <code>GET /connectors/{source}/tables</code>. Mapping one is a
        governed proposal, reviewed on Pending apps — nothing here reads a
        table any differently until a governor approves it.
      </Typography>

      {failure ? (
        <Alert
          severity={failure.isManifest ? "info" : "error"}
          variant="outlined"
          sx={{ mb: 1 }}
        >
          {failure.isManifest
            ? "This console does not declare connector.tables, so the list of tables cannot be shown."
            : failure.message}
        </Alert>
      ) : null}

      {mappedFailure ? (
        <Alert
          severity={mappedFailure.isManifest ? "info" : "error"}
          variant="outlined"
          sx={{ mb: 1 }}
        >
          Whether any of these tables already has an approved mapping could
          not be checked:{" "}
          {mappedFailure.isManifest
            ? "this console does not declare connector.map.approve."
            : mappedFailure.message}
        </Alert>
      ) : null}

      {mapped.length ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {mapped.map((m) => (
            <SyncMapRow
              key={m.table}
              source={source}
              mapping={m}
              post={post}
              onToggled={onToggled}
            />
          ))}
        </Stack>
      ) : null}

      {tables && !failure ? (
        unmapped.length ? (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {unmapped.map((t) => (
              <Chip
                key={t}
                size="small"
                variant="outlined"
                label={t}
                sx={{ fontFamily: "monospace" }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Every table this connector can reach already has an approved
            mapping.
          </Typography>
        )
      ) : null}

      <MappingBuilder
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        get={get}
        post={post}
        source={source}
        tables={unmapped}
        onProposed={onProposed}
      />
    </>
  );
}

/**
 * A registry connector's own mapping, browsable without a failure first
 * (LO-173). LO-172 built the correction mechanism and the only door into it
 * was a held_reject card — an override already applies to every record of a
 * concept once approved, keyed by `(source, concept)` rather than by the
 * one record that happened to fail, so tying the entry point to a failure
 * was never the mechanism, only the UI's one way in. This is the second
 * door: every concept this connector declares, whichever already carry a
 * live correction, and a button to propose one for any of them.
 *
 * `mapping.py`'s field functions themselves stay out of this screen on
 * purpose — they are locked, reviewed Python, not something this console
 * pretends to let you edit as if it were data.
 */
function ConnectorMappingOverrides({ get, post, source }) {
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState(null);
  const [proposing, setProposing] = useState(null); // concept string, or null
  const [outcome, setOutcome] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setData(null);
    setFailure(null);
    get(`/connectors/${encodeURIComponent(source)}/overrides`)
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setFailure(e);
      });
    return () => {
      cancelled = true;
    };
  }, [get, source]);

  useEffect(() => load(), [load]);

  const concepts = data?.concepts || [];

  return (
    <>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        Mapping corrections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        What each concept above becomes is a reviewed, locked function — not
        edited here. What can be proposed is a narrower thing: fill one blank
        field from another or from a fixed value, before that function runs.
        Applies to every record of the concept once approved, not only one
        that already failed.
      </Typography>

      {failure ? (
        <Alert severity={failure.isManifest ? "info" : "error"} variant="outlined" sx={{ mb: 1 }}>
          {failure.isManifest
            ? "This console does not declare connector.map_override.approve, so corrections cannot be shown here."
            : failure.message}
        </Alert>
      ) : null}

      {outcome ? (
        <Alert severity="success" variant="outlined" sx={{ mb: 1 }} onClose={() => setOutcome(null)}>
          Proposed. A governor decides it on the Requests screen — see{" "}
          <code>{outcome.request_id}</code>.
        </Alert>
      ) : null}

      {data && !failure ? (
        <Stack spacing={1}>
          {concepts.map((c) => (
            <Paper key={c.concept} variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <Typography sx={{ fontFamily: "monospace" }}>{c.concept}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    &rarr; {c.type}
                  </Typography>
                  <Chip
                    size="small"
                    variant={c.override ? "filled" : "outlined"}
                    color={c.override ? "primary" : "default"}
                    label={c.override ? "correction live" : "no correction"}
                  />
                </Stack>

                {c.override ? (
                  <Disclose summary="What it fills" label="Live correction" align="inline">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {c.override.reason}
                    </Typography>
                    <OverrideRulesTable rules={c.override.rules} />
                  </Disclose>
                ) : null}

                <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                  <Button size="small" variant="outlined" onClick={() => setProposing(c.concept)}>
                    Propose a correction
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : null}

      <ProposeOverrideDialog
        open={Boolean(proposing)}
        source={source}
        concept={proposing || ""}
        payloadKeys={[]}
        post={post}
        onClose={() => setProposing(null)}
        onProposed={(result) => {
          setOutcome(result);
          load();
        }}
      />
    </>
  );
}

/**
 * One writer in full: what it wrote, what the last run did, how often it
 * runs, and what it turns each source concept into.
 *
 * Everything here was on the card. It is here instead because the register
 * above is a thing you scan and this is a thing you read — and because the
 * schedule editor is a form, and a form belongs somewhere its Save button is
 * unambiguously about the thing in front of you.
 */
function WriterDrawer({
  writer, unknown, onSchedule, onClose, get, post, mapped, mappedFailure,
  onToggled, onProposed, mayGovern, onRollback,
}) {
  // A custom app's writer card is a Cognito client id (`source_system` on
  // whatever it wrote), and mapping its own tables needs the control
  // plane's own instance name instead — `<app>-<instance>-db-<table>`, the
  // shape `simpleapp.yaml` actually names tables with. Nothing links the
  // two today (LO-79 predates the assistant's own apps), so this is typed
  // rather than derived: the Apps screen, or the instance itself, is where
  // that name is already shown.
  const [instance, setInstance] = useState("");

  useEffect(() => {
    setInstance("");
  }, [writer?.source]);

  if (!writer) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const state = writer.state;
  const kind = KIND[writer.kind] || KIND.unknown;
  const types = Object.entries(writer.objects || {});
  const mappingSource = writer.kind === "connector"
    ? writer.source
    : instance.trim() && writer.app_type
      ? `${writer.app_type}:${instance.trim()}`
      : "";

  return (
    <Drawer
      anchor="right"
      open={Boolean(writer)}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 620 }, maxWidth: "100%" } },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <DrawerHead
          title={writer.label || writer.source}
          id={writer.label ? writer.source : null}
          onClose={onClose}
          lede={
            <>
              {kind.label} — {kind.blurb} This panel is everything about one
              source of records: what it has put into this kernel, when it last
              ran, how often it runs, and what each thing on the far side turns
              into over here.
            </>
          }
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1.5, sm: 4 }}
          sx={{ mb: 2 }}
        >
          <Fact label="Records it has put in">
            {writer.has_written ? (
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                {types.map(([type, n]) => (
                  <Chip key={type} size="small" label={`${type} ${n}`} />
                ))}
                {writer.edges ? (
                  <Tooltip title="Links between records — an invoice to the customer it belongs to, a case to the person it is about. Created alongside the records themselves.">
                    <Chip
                      size="small"
                      label={`${writer.edges} links`}
                      variant="outlined"
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                none
              </Typography>
            )}
          </Fact>

          {writer.kind === "connector" ? (
            <Fact label="How far it got">
              {state?.cursor ? (
                <Tooltip title={String(state.cursor)}>
                  <Typography variant="body2" sx={{ cursor: "help" }}>
                    Picks up where it left off
                  </Typography>
                </Tooltip>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {state ? "Reads everything, every time" : "—"}
                </Typography>
              )}
            </Fact>
          ) : null}
        </Stack>

        {writer.kind === "connector" && !state ? (
          <Box sx={{ mb: 2 }}>
            <Fact label="Last run">
              <Unknown reason={unknown.never_run} />
            </Fact>
          </Box>
        ) : null}

        {state ? (
          <LastRun
            state={state}
            runs={writer.runs || []}
            source={writer.source}
            mayGovern={mayGovern}
            onRollback={onRollback}
          />
        ) : null}

        {writer.kind === "connector" && onSchedule ? (
          <Schedule
            source={writer.source}
            schedule={writer.schedule}
            onSave={(body) => onSchedule(writer.source, body)}
          />
        ) : null}

        {writer.kind === "app" ? (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                A custom app's own tables have no automatic schedule yet —
                an approved mapping here runs when an operator runs{" "}
                <code>connectors/run_sync.py {writer.app_type} --instance
                &lt;name&gt;</code>, not on a clock.
              </Typography>
            </Alert>
            <TextField
              size="small"
              label="Instance name"
              helperText="From the Apps screen, or whoever installed it — not the id above, which is this app's Cognito client."
              value={instance}
              onChange={(e) => setInstance(e.target.value)}
              sx={{ mt: 1.5, maxWidth: "20rem" }}
            />
          </Box>
        ) : null}

        {writer.concepts?.length ? (
          <>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
              What it pulls, and what each becomes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              What the far system calls a thing, on the left; what it is stored
              as here, in the middle; and on the right the single permission
              that writing it uses. Anything this source sends that is not on
              this list is rejected rather than guessed at.
            </Typography>
            {/* Four columns, two of them monospace ids, and 375px of phone.
                It scrolls here rather than pushing the whole console sideways
                — which it did, and which the 390px check caught. */}
            <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableBody>
                {writer.concepts.map((c) => (
                  <TableRow key={c.concept}>
                    <TableCell sx={{ border: 0, fontFamily: "monospace", width: 220 }}>
                      {c.concept}
                    </TableCell>
                    <TableCell sx={{ border: 0, width: 30, color: "text.secondary" }}>
                      &rarr;
                    </TableCell>
                    <TableCell sx={{ border: 0 }}>{c.type}</TableCell>
                    <TableCell
                      sx={{ border: 0, fontFamily: "monospace", color: "text.secondary" }}
                    >
                      {c.action}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          </>
        ) : null}

        {writer.kind === "connector" && get && post ? (
          <ConnectorMappingOverrides
            get={get}
            post={post}
            source={writer.source}
          />
        ) : null}

        {mappingSource && get && post ? (
          <TablesToMap
            get={get}
            post={post}
            source={mappingSource}
            mapped={mapped || []}
            mappedFailure={mappedFailure}
            onToggled={onToggled}
            onProposed={onProposed}
          />
        ) : null}
      </Box>
    </Drawer>
  );
}

export default function Connectors({ get, post, claims, session, onEffective }) {
  const [data, setData] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  // The one control-plane fact this screen needs: whether an app that has
  // never written anything is still worth showing "off, never written" for.
  // A custom app the kernel still holds a pointer for but nothing is running
  // any more is not "off" in the sense this screen means it — it is torn
  // down, and its pointer just was never withdrawn (LO-111 found this exact
  // gap once already, for a different screen). A writer that has actually
  // written stays regardless: that is a real record in the graph and this
  // screen never hides one on account of what became of the app afterwards.
  const deploy = useDeployments({ session, get, post, onEffective });

  // Which tables already have an approved sync mapping, by registry key —
  // read from `GET /connector-map-requests` rather than a dedicated list
  // endpoint, because none exists (see `TablesToMap`'s own comment). A
  // failure here is kept separately from the connectors failure above: this
  // screen still works with no sync-map data at all, just with an honest
  // gap where "already mapped" would be.
  const [syncmapsBySource, setSyncmapsBySource] = useState({});
  const [syncmapsFailure, setSyncmapsFailure] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await get("/connectors"));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    } finally {
      setLoading(false);
    }
  }, [get]);

  const loadSyncmaps = useCallback(async () => {
    try {
      const body = await get("/connector-map-requests");
      const bySource = {};
      for (const r of body?.requests || []) {
        if (r.state !== "approved") continue;
        const d = r.declaration || {};
        if (!d.source || !d.table) continue;
        (bySource[d.source] ||= []).push({ ...d, request_id: r.request_id });
      }
      setSyncmapsBySource(bySource);
      setSyncmapsFailure(null);
    } catch (e) {
      setSyncmapsBySource({});
      setSyncmapsFailure(e);
    }
  }, [get]);

  useEffect(() => {
    load();
    loadSyncmaps();
    // This screen is about the store, not about records of a type, so there
    // is no tag intersection to report. Saying so beats leaving the strip
    // showing whatever the last section resolved.
    onEffective(null);
  }, [load, loadSyncmaps, onEffective]);

  /** A toggle succeeded against the mapping the drawer currently has open.
   * Applied locally rather than reloaded, because there is nothing to
   * reload it from — see `SyncMapRow`'s comment on why. */
  function handleToggled(table, enabled) {
    if (!open) return;
    const key = open.kind === "connector"
      ? registryKeyOf(open.source)
      : open.app_type;
    setSyncmapsBySource((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((m) =>
        m.table === table ? { ...m, enabled } : m),
    }));
  }

  if (loading && !data) return <Loading label="Reading the connectors" />;

  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }

  const allWriters = data?.writers || [];
  // "Off, never written" is a claim about an app that could write here right
  // now. Once its every instance is torn down that stops being true, and the
  // kernel has no way to know it — its pointer is still there, the pointer
  // withdrawal a teardown ought to trigger does not always happen (LO-111),
  // and the kernel does not read the control plane in any case. So this
  // screen checks the one thing it can: whether the app type this writer
  // names still has something running. A writer that has actually written is
  // never hidden by this — that is a graph fact this screen has never hidden
  // on account of what became of the writer afterwards, and it stays that way.
  const writers = allWriters.filter((w) => {
    if (w.kind !== "app" || w.has_written) return true;
    return deploy.deploymentsOf(w.app_type)
      .some((d) => d.desiredState !== "absent");
  });
  const unknown = data?.unknown || {};
  const unrecognised = writers.filter((w) => w.kind === "unknown");

  const records = writers.reduce((n, w) => n + (w.total || 0), 0);
  const connectors = writers.filter((w) => w.kind === "connector").length;
  const tally = data?.tally;
  const counted = tally?.state !== "not_run";

  return (
    <Stack spacing={2}>
      <Headline
        eyebrow={tallyEyebrow(tally)}
        figure={counted ? commas(records) : "—"}
        caption={
          counted
            ? `records, from ${connectors} connector${
                connectors === 1 ? "" : "s"
              } and ${writers.length - connectors} app${
                writers.length - connectors === 1 ? "" : "s"
              }`
            : "records, not counted yet"
        }
        basis={`Counted by the kernel out of its own graph, not taken from what a writer reported it did. A writer that claims two thousand rows and a graph that holds eight are two different claims and only one of them is checkable from here. ${tally?.detail || ""}`}
      />

      {unrecognised.length ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>
            {unrecognised.length} writer
            {unrecognised.length === 1 ? "" : "s"} nobody can account for
          </AlertTitle>
          Not a registered connector, and no manifest in the kernel. Same
          reasoning as the bypass check reporting a row shape nothing writes:
          the writer you cannot explain is the interesting one.
        </Alert>
      ) : null}

      <Disclose
        summary="Every writer that has put something in this graph, connector or not."
        label="What counts as a writer"
      >
        <Typography variant="body2" color="text.secondary">
          A connector is declared in the mapping registry; an app wrote through{" "}
          <code>/actions</code> and is named by its client id, because{" "}
          <code>(source_system, source_id)</code> is the upsert key. A writer
          that is neither is listed as unrecognised rather than dropped — the
          writer you cannot account for is the interesting one. Counts are of
          the records the kernel found in its own graph when it last counted,
          rather than a writer's report of what it did, and the time is on the
          figure. {tally?.detail}
        </Typography>
      </Disclose>

      {/* This used to say three things could not be known. LO-48 recorded
          them, so the banner is gone rather than kept as a stale caveat —
          leaving it would be exactly the "green tick that overstates" in
          reverse. */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fit, minmax(280px, 1fr))",
          },
          alignItems: "stretch",
        }}
      >
        {writers.map((w) => (
          <WriterCard
            key={w.source}
            writer={w}
            self={claims?.client_id}
            onOpen={setOpen}
            tally={tally}
          />
        ))}
      </Box>

      <WriterDrawer
        writer={open}
        unknown={unknown}
        onClose={() => setOpen(null)}
        onSchedule={
          post
            ? async (source, body) => {
                await post(`/connectors/${source}/schedule`, body);
                // Re-read rather than patching state locally: the kernel
                // may have refused part of what was sent, and a screen
                // that shows what it asked for rather than what was
                // stored is the whole failure this product is against.
                await load();
                setOpen(null);
              }
            : null
        }
        get={get}
        post={post}
        mapped={
          open
            ? syncmapsBySource[
                open.kind === "connector"
                  ? registryKeyOf(open.source)
                  : open.app_type
              ] || []
            : []
        }
        mappedFailure={syncmapsFailure}
        onToggled={handleToggled}
        onProposed={loadSyncmaps}
        mayGovern={data?.may_decide}
        onRollback={
          post
            ? async (source, runId) => (
                // No `load()` after this one, unlike `onSchedule`: a
                // rollback changes the business records a run wrote, never
                // the run's own history or this screen's counts of it, so
                // there is nothing in `/connectors` a reload would change.
                await post(`/connectors/${source}/runs/${runId}/rollback`, {})
              )
            : null
        }
      />
    </Stack>
  );
}
